import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { LockedClause } from '../locked-clause';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';

import { Checkbox } from '../checkbox-node';
import { Signature } from '../signature-node';
import { SpecialConditions } from '../special-conditions-node';
import { Annexure } from '../annexus-node';

import { DateField } from '../date-node';
import { TextField } from '../text-node';
import { Placeholder } from '../placeholder-node';
import Gapcursor from '@tiptap/extension-gapcursor';
import HardBreak from '@tiptap/extension-hard-break';
import { Extension } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import { RadioGroup } from '../radio-button-node';
import { CommentExtension, CommentMark } from '../comment-mark';

const EnterAsLineBreak = Extension.create({
  name: 'enter-as-line-break',

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        // inserts <br/> instead of new paragraph
        editor.chain().focus().setHardBreak().run();
        return true;
      },
    };
  },
});
// Heuristics: promote paragraph nodes that look like headings into heading nodes
const isHeadingText = (text, marks) => {
  if (!text) return false;
  // numeric sections: "1.", "1)", "1.1" etc.
  if (/^\s*\d+(?:[.)]|(?:\.\d+))+?\s*/.test(text)) return true;
  // if the text has bold/strong marks, it's more likely a heading
  if (marks && Array.isArray(marks) && marks.some((m) => m.type === 'bold'))
    return true;

  // short title-case or ALL CAPS lines
  if (/^[A-Z][a-z0-9]+(?:\s+[A-Z][a-z0-9]+){0,5}$/.test(text)) return true;
  if (/^[A-Z\s-]{3,}$/.test(text) && text.trim().length < 60) return true;
  return false;
};

const promoteParagraphsToHeadings = (doc) => {
  if (!doc) return doc;
  const copy = JSON.parse(JSON.stringify(doc));
  const nodes = Array.isArray(copy.content)
    ? copy.content
    : copy.content && Array.isArray(copy.content.content)
      ? copy.content.content
      : [];

  copy.content = nodes.map((node) => {
    if (
      node.type === 'paragraph' &&
      Array.isArray(node.content) &&
      node.content.length === 1 &&
      node.content[0].type === 'text'
    ) {
      const txt = (node.content[0].text || '').trim();
      const marks = node.content[0].marks || [];
      if (isHeadingText(txt, marks) && txt.length < 120) {
        return { type: 'heading', attrs: { level: 2 }, content: node.content };
      }
    }
    return node;
  });

  return copy;
};

// Remove empty text nodes and recursively clean the document.
const sanitizeTiptapJSON = (node) => {
  if (!node) return node;

  // If it's a text node, drop if empty string
  if (node.type === 'text') {
    if (typeof node.text !== 'string') return null;
    if (node.text.trim() === '') return null;
    return node;
  }

  const copy = Array.isArray(node)
    ? JSON.parse(JSON.stringify(node))
    : JSON.parse(JSON.stringify(node));

  if (copy.content && Array.isArray(copy.content)) {
    const cleaned = copy.content
      .map((n) => sanitizeTiptapJSON(n))
      .filter((n) => n !== null && n !== undefined);

    copy.content = cleaned;

    // If this node requires content (like paragraph/heading) but has no children now,
    // return null to avoid empty container nodes that TipTap rejects.
    if (cleaned.length === 0 && copy.type !== 'doc') {
      return null;
    }
  }

  return copy;
};

// Normalize various nested content shapes into a tiptap doc
const normalizeContent = (c) => {
  if (!c) return null;
  if (c.type === 'doc' && Array.isArray(c.content)) return c;
  if (Array.isArray(c.content)) return { type: 'doc', content: c.content };
  if (c.content && Array.isArray(c.content.content))
    return { type: 'doc', content: c.content.content };
  if (Array.isArray(c)) return { type: 'doc', content: c };
  return null;
};

export default function DocsCreator({
  data,
  setData,
  onEditorReady,
  activePage,
  setActivePage,
  setCommentHandler,
}) {
  const [comments, setComments] = useState([]);
  const [activeComment, setActiveComment] = useState(null);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextStyle,
      Link.configure({ openOnClick: true }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      RadioGroup,
      Table,
      TableRow,
      TableCell,
      TableHeader,

      Checkbox,
      LockedClause,
      Signature,
      DateField,
      TextField,
      Gapcursor,
      Placeholder,
      HardBreak,
      SpecialConditions,
      Annexure,
      EnterAsLineBreak,
      CommentExtension,
      CommentMark,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: null,
    editable: true,
  });
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingCommentRange, setPendingCommentRange] = useState(null);
  const [commentText, setCommentText] = useState('');
  const suppressApplyRef = useRef(false);
  const lastAppliedRef = useRef(null);
  const [bubblePos, setBubblePos] = useState(null);
  const applyWithSelection = (content, isJSON = true) => {
    try {
      const sel = editor && editor.state && editor.state.selection;
      const from = sel ? sel.from : 0;
      const to = sel ? sel.to : from;
      if (isJSON) editor.commands.setContent(content, false);
      else editor.commands.setContent(content || '', false);
      const docSize = editor.state.doc.content.size;
      const clampedFrom = Math.min(Math.max(0, from), docSize);
      const clampedTo = Math.min(Math.max(0, to), docSize);
      try {
        editor
          .chain()
          .focus()
          .setTextSelection({ from: clampedFrom, to: clampedTo })
          .run();
      } catch (e) {
        console.log('error', e);
      }
    } catch (err) {
      console.log('error', err);
    }
  };
  useEffect(() => {
    if (setCommentHandler) {
      setCommentHandler(() => handleAddCommentClick);
    }
  }, [editor]);
  const extractCommentsFromDoc = (doc) => {
    const comments = [];

    doc.descendants((node, pos) => {
      if (node.marks) {
        node.marks.forEach((mark) => {
          if (mark.type.name === 'commentMark') {
            comments.push({
              id: mark.attrs.commentId,
              from: pos,
              to: pos + node.nodeSize,
            });
          }
        });
      }
    });

    return comments;
  };
  useEffect(() => {
    if (!editor) return;

    const doc = editor.state.doc;
    const extracted = extractCommentsFromDoc(doc);

    setComments((prev) => {
      const map = new Map(prev.map((c) => [c.id, c]));

      extracted.forEach((c) => {
        if (!map.has(c.id)) {
          map.set(c.id, {
            ...c,
            text: 'Recovered comment',
            selectedText: editor.state.doc.textBetween(c.from, c.to),
            replies: [],
            author: 'Unknown',
            createdAt: new Date().toISOString(),
          });
        }
      });

      return Array.from(map.values());
    });
  }, [editor, activePage]);
  useEffect(() => {
    if (!editor) return;

    const updatePageContent = () => {
      if (suppressApplyRef.current) return;

      let html = editor.getHTML();
      let json = editor.getJSON();
      // promote paragraph-like headings into heading nodes
      try {
        json = promoteParagraphsToHeadings(json);
      } catch (e) {
        console.log('error ' + e.message, 'error');
      }

      try {
        const cleaned = sanitizeTiptapJSON(json);
        if (cleaned && cleaned.type === 'doc') json = cleaned;
      } catch (e) {
        console.log('error ' + e.message, 'error');
      }

      // Ensure tables/images have responsive styles so exported HTML fits pages
      if (html) {
        // add inline styles to tables and cells when missing
        html = html.replace(/<table(.*?)>/gi, (m, attrs) => {
          if (/style=/i.test(attrs)) return `<table${attrs}>`;
          return `<table${attrs} style="width:100%;table-layout:fixed;border-collapse:collapse;">`;
        });

        html = html.replace(/<(td|th)(.*?)>/gi, (m, tag, attrs) => {
          if (/style=/i.test(attrs)) return `<${tag}${attrs}>`;
          return `<${tag}${attrs} style="word-break:break-word;white-space:normal;padding:6px;border:1px solid #e5e7eb;">`;
        });
      }

      try {
        const serialized = JSON.stringify(json || {});
        // avoid re-writing identical content
        const prevPage =
          (data && data.pages && data.pages.find((p) => p.id === activePage)) ||
          null;
        const prevSerialized =
          prevPage && prevPage.content
            ? JSON.stringify(prevPage.content)
            : null;
        if (serialized === prevSerialized) return;

        // record the content we are about to write so apply-effect won't re-apply it
        lastAppliedRef.current = serialized;
      } catch (err) {
        console.log('error ' + err.message, 'error');
      }

      setData((prev) => ({
        ...prev,
        pages: prev.pages.map((p) =>
          p.id === activePage
            ? { ...p, content: json, html } // 👈 STORE HTML HERE
            : p,
        ),
      }));
    };

    editor.on('update', updatePageContent);
    const onSelection = () => {
      try {
        const sel = editor.state.selection;
        console.debug('[DocsCreator] selectionUpdate', {
          from: sel.from,
          to: sel.to,
          anchor: sel.anchor,
          head: sel.head,
          docSize: editor.state.doc.content.size,
          lastApplied: !!lastAppliedRef.current,
        });
      } catch (e) {
        console.log('error', e);
      }
    };
    editor.on('selectionUpdate', onSelection);

    // Attach dragstart listener to editor DOM to expose friendly drag payloads
    const dom = editor.view?.dom;
    const onDragStart = (ev) => {
      try {
        const target = ev.target;
        // placeholder spans have data-placeholder attribute
        const ph = target.closest && target.closest('[data-placeholder]');
        if (ph) {
          const key = ph.getAttribute('data-placeholder') || 'field';
          const info = JSON.stringify({
            hint: 'placeholder',
            key,
            label: ph.textContent || key,
          });
          // don't set application/x-rp-editor here — NodeViews will provide correct editor-origin payloads with positions
          console.debug('[Editor] dragstart placeholder (hint)', info);
          return;
        }

        // checkbox and other nodeviews may have data-rp-node attr
        const nodeEl = target.closest && target.closest('[data-rp-node]');
        if (nodeEl) {
          const tn = nodeEl.getAttribute('data-rp-node');
          console.debug('[Editor] dragstart node (hint)', tn);
          return;
        }
      } catch (err) {
        console.log('error', err);
      }
    };

    if (dom && dom.addEventListener)
      dom.addEventListener('dragstart', onDragStart);

    return () => {
      editor.off('update', updatePageContent);
      try {
        editor.off('selectionUpdate', onSelection);
      } catch (e) {
        console.log('error ' + e.message, 'error');
      }
      try {
        if (dom && dom.removeEventListener)
          dom.removeEventListener('dragstart', onDragStart);
      } catch (e) {
        console.log('error ' + e.message, 'error');
      }
    };
  }, [editor, activePage, data && data.pages]);
  useEffect(() => {
    if (!editor || !data || !data.pages || !data.pages.length) return;

    if (suppressApplyRef.current) {
      // a programmatic apply is in progress or just completed; reset and skip
      suppressApplyRef.current = false;
      return;
    }

    const page = data.pages.find((p) => p.id === activePage);
    if (!page) return;
    console.debug('[DocsCreator] applying page content', {
      activePage,
      pageId: page.id,
      hasContent: !!page.content,
      hasHtml: !!page.html,
    });

    const normalized = normalizeContent(page.content);
    if (normalized) {
      try {
        const cleaned = sanitizeTiptapJSON(normalized);
        const serialized = cleaned ? JSON.stringify(cleaned) : null;
        if (serialized && lastAppliedRef.current === serialized) {
          // already applied
          return;
        }

        if (cleaned) {
          console.debug(
            '[DocsCreator] setContent: JSON (cleaned) for',
            activePage,
          );
          suppressApplyRef.current = true;
          applyWithSelection(cleaned, true);
          lastAppliedRef.current = serialized;
          setTimeout(() => (suppressApplyRef.current = false), 60);
        } else if (page.html) {
          console.debug(
            '[DocsCreator] setContent: HTML fallback for',
            activePage,
          );
          suppressApplyRef.current = true;
          applyWithSelection(page.html, false);
          lastAppliedRef.current = page.html;
          setTimeout(() => (suppressApplyRef.current = false), 60);
        } else {
          console.debug('[DocsCreator] setContent: empty for', activePage);
          suppressApplyRef.current = true;
          applyWithSelection('', false);
          lastAppliedRef.current = null;
          setTimeout(() => (suppressApplyRef.current = false), 60);
        }
      } catch (e) {
        console.debug(
          '[DocsCreator] setContent: exception, using html/empty',
          e,
        );
        if (page.html) {
          suppressApplyRef.current = true;
          applyWithSelection(page.html, false);
          lastAppliedRef.current = page.html;
          setTimeout(() => (suppressApplyRef.current = false), 60);
        } else {
          suppressApplyRef.current = true;
          applyWithSelection('', false);
          lastAppliedRef.current = null;
          setTimeout(() => (suppressApplyRef.current = false), 60);
        }
      }
    } else if (page.html) {
      console.debug(
        '[DocsCreator] setContent: page.html branch for',
        activePage,
      );
      suppressApplyRef.current = true;
      applyWithSelection(page.html, false);
      lastAppliedRef.current = page.html;
      setTimeout(() => (suppressApplyRef.current = false), 60);
    } else {
      console.debug(
        '[DocsCreator] setContent: page empty branch for',
        activePage,
      );
      suppressApplyRef.current = true;
      applyWithSelection('', false);
      lastAppliedRef.current = null;
      setTimeout(() => (suppressApplyRef.current = false), 60);
    }
  }, [editor, activePage, data && data.pages]);

  // If an uploaded document provides a single HTML blob with page-break markers,
  // split it into separate pages and convert each slice into tiptap JSON.
  useEffect(() => {
    if (!editor || !data || !data.pages || !data.pages.length) return;

    // Find a page that has html but no tiptap JSON content
    const candidate = data.pages.find((p) => p.html && !p.content);
    if (!candidate) return;

    const html = candidate.html;

    // Common page-break markers from docx -> html conversions
    const pageBreakRegex =
      /(<div[^>]*style=["'][^"']*page-break-after[^"']*["'][^>]*>|<div[^>]*class=["']?page-break["']?[^>]*>|<hr[^>]*class=["']?page-break["']?[^>]*>|<br[^>]*style=["'][^"']*page-break-after[^"']*["'][^>]*>|<!--\s*PAGE_BREAK\s*-->)/gi;

    if (!pageBreakRegex.test(html)) return;

    // split into parts
    const tokenized = html.replace(pageBreakRegex, '<!--PAGE_BREAK-->');
    const parts = tokenized
      .split('<!--PAGE_BREAK-->')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;

    const injectTableStyles = (h) => {
      if (!h) return h;
      let out = h;
      out = out.replace(/<table(.*?)>/gi, (m, attrs) => {
        if (/style=/i.test(attrs)) return `<table${attrs}>`;
        return `<table${attrs} style="width:100%;table-layout:fixed;border-collapse:collapse;">`;
      });
      out = out.replace(/<(td|th)(.*?)>/gi, (m, tag, attrs) => {
        if (/style=/i.test(attrs)) return `<${tag}${attrs}>`;
        return `<${tag}${attrs} style="word-break:break-word;white-space:normal;padding:6px;border:1px solid #e5e7eb;">`;
      });
      return out;
    };

    // build new pages by converting each HTML slice to tiptap JSON via the editor
    const newPages = [];
    parts.forEach((part, idx) => {
      const pageHtml = injectTableStyles(part);

      // set editor content to the HTML slice and read JSON
      try {
        editor.commands.setContent(pageHtml, false);
      } catch (e) {
        // fallback: set empty
        editor.commands.setContent('', false);
      }

      let json = editor.getJSON();
      try {
        json = promoteParagraphsToHeadings(json);
      } catch (e) {
        console.log('error', e);
      }

      try {
        const cleaned = sanitizeTiptapJSON(json);
        if (cleaned && cleaned.type === 'doc') json = cleaned;
      } catch (e) {
        console.log('error', e);
      }

      newPages.push({
        id: `page-${idx + 1}`,
        html: pageHtml,
        content: json,
      });
    });

    // update the document pages and set active page to first
    setData((prev) => ({
      ...prev,
      pages: newPages,
    }));
    try {
      if (typeof setActivePage === 'function' && newPages[0]) {
        console.debug(
          '[DocsCreator] split pages: setting active to',
          newPages[0].id,
        );
        setActivePage(newPages[0].id);
      }
    } catch (e) {
      console.log('error', e);
    }

    // restore editor to first page content
    if (newPages[0]) {
      console.debug(
        '[DocsCreator] split pages: restoring editor to first page',
      );
      try {
        const cleaned = sanitizeTiptapJSON(newPages[0].content);
        if (cleaned) applyWithSelection(cleaned, true);
        else applyWithSelection(newPages[0].html || '', false);
      } catch (e) {
        applyWithSelection(newPages[0].html || '', false);
      }
    }

    // set active page in parent by dispatching a synthetic event: parent listens via prop
    // If parent provided onEditorReady it already has setEditor; TemplateCreator holds activePage state
    // We can't set parent's activePage directly here — rely on the caller to pick first page.
    // Optionally: if prev activePage not present in newPages, nothing to do.
  }, [editor, data, setData, activePage]);

  // notify parent when editor is ready
  useEffect(() => {
    if (editor && typeof onEditorReady === 'function') {
      console.debug('[DocsCreator] editor ready, notifying parent', {
        activePage,
      });
      onEditorReady(editor);
    }
    // also attempt to apply content immediately when editor becomes available
    try {
      if (editor && data && data.pages && data.pages.length) {
        console.debug(
          '[DocsCreator] editor ready: attempting to apply current page',
        );
        const page = data.pages.find((p) => p.id === activePage);
        if (page) {
          const normalized = normalizeContent(page.content);
          if (normalized) {
            const cleaned = sanitizeTiptapJSON(normalized);
            if (cleaned) {
              suppressApplyRef.current = true;
              editor.commands.setContent(cleaned, false);
              lastAppliedRef.current = JSON.stringify(cleaned);
              setTimeout(() => (suppressApplyRef.current = false), 60);
            } else if (page.html) {
              suppressApplyRef.current = true;
              editor.commands.setContent(page.html, false);
              lastAppliedRef.current = page.html;
              setTimeout(() => (suppressApplyRef.current = false), 60);
            } else {
              suppressApplyRef.current = true;
              editor.commands.setContent('', false);
              lastAppliedRef.current = null;
              setTimeout(() => (suppressApplyRef.current = false), 60);
            }
          } else if (page.html) {
            suppressApplyRef.current = true;
            editor.commands.setContent(page.html, false);
            lastAppliedRef.current = page.html;
            setTimeout(() => (suppressApplyRef.current = false), 60);
          }
        }
      }
    } catch (e) {
      console.debug('[DocsCreator] error applying page on editor ready', e);
    }
  }, [editor, onEditorReady]);
  function CommentsPanel({
    comments,
    activeComment,
    setActiveComment,
    onReply,
  }) {
    const active = comments.find((c) => c.id === activeComment);

    if (!active) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3">
        {/* Modal */}
        <div className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-slate-50">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-[#375AE7] text-sm">
                💬
              </div>
              <span className="font-semibold text-slate-700">Comments</span>
            </div>

            <button
              onClick={() => setActiveComment(null)}
              className="text-slate-400 hover:text-slate-700 transition"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {/* Main Comment */}
            <div className="bg-slate-50 rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-[#375AE7] text-xs font-semibold">
                    {active.author?.charAt(0)}
                  </div>
                  <span className="font-medium text-slate-600">
                    {active.author}
                  </span>
                </div>

                <span>
                  {new Date(active.createdAt).toLocaleDateString()} ·{' '}
                  {new Date(active.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>

              {/* Selected Text */}
              {active.selectedText && (
                <div className="mt-2 text-[11px] italic text-slate-400 border-l-2 border-blue-300 pl-2">
                  “{active.selectedText.slice(0, 120)}”
                </div>
              )}

              {/* Comment Text */}
              <p className="text-sm mt-2 text-slate-700 leading-relaxed">
                {active.text || 'No comment provided'}
              </p>
            </div>

            {/* Replies */}
            <div className="space-y-2">
              {active.replies?.map((r, i) => (
                <div
                  key={i}
                  className="flex gap-2 items-start bg-white border rounded-lg px-3 py-2 shadow-sm"
                >
                  <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">
                    {r.author?.charAt(0)}
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span className="font-medium text-slate-600">
                        {r.author}
                      </span>
                      <span>
                        {new Date(active.createdAt).toLocaleDateString()} ·{' '}
                        {new Date(r.createdAt || Date.now()).toLocaleTimeString(
                          [],
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )}
                      </span>
                    </div>

                    <p className="text-sm text-slate-700">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="border-t p-3 bg-slate-50">
            <div className="flex items-center gap-2">
              <input
                placeholder="Write a reply..."
                className="flex-1 text-sm border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    onReply(active.id, e.target.value);
                    e.target.value = '';
                  }
                }}
              />

              <button
                onClick={(e) => {
                  const input = e.currentTarget.previousSibling;
                  if (input.value.trim()) {
                    onReply(active.id, input.value);
                    input.value = '';
                  }
                }}
                className="bg-[#375AE7] hover:bg-[#375AE7] text-white px-4 py-2 rounded-full text-sm transition shadow"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const focusComment = (commentId) => {
    const { doc } = editor.state;

    let from = null;
    let to = null;

    doc.descendants((node, pos) => {
      node.marks?.forEach((m) => {
        if (m.type.name === 'commentMark' && m.attrs.commentId === commentId) {
          if (from === null) from = pos;
          to = pos + node.nodeSize;
        }
      });
    });

    if (from !== null) {
      editor.chain().focus().setTextSelection({ from, to }).run();
    }
  };
  const onReply = (commentId, text) => {
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? {
              ...c,
              replies: [
                ...(c.replies || []),
                {
                  text,
                  author: 'You',
                  createdAt: new Date().toISOString(),
                },
              ],
            }
          : c,
      ),
    );
  };
  const handleAddCommentClick = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;

    if (from === to) return;

    // store immediately BEFORE any state updates
    setPendingCommentRange({ from, to });

    setShowCommentModal(true);
  };
  const saveComment = () => {
    if (!editor || !pendingCommentRange) return;

    const id = crypto.randomUUID();

    editor
      .chain()
      .focus()
      .setTextSelection(pendingCommentRange)
      .setMark('commentMark', { commentId: id })
      .run();

    const { from, to } = pendingCommentRange;

    const selectedText = editor.state.doc.textBetween(from, to, ' ');

    setComments((prev) => [
      ...prev,
      {
        id,
        from: pendingCommentRange.from,
        to: pendingCommentRange.to,
        text: commentText,
        selectedText, // 👈 ADD THIS
        author: 'You',
        createdAt: new Date().toISOString(),
        replies: [],
      },
    ]);

    setShowCommentModal(false);
    setCommentText('');
    setPendingCommentRange(null);
  };
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail.id;

      const comment = comments.find((c) => c.id === id);

      if (!comment) {
        console.warn('Comment not found in state:', id);
        return;
      }

      setActiveComment(id);
      focusComment(id); // ✅ pass id not object
    };
    localStorage.setItem(
      'contract-doc',
      JSON.stringify({
        ...data,
        pages: data.pages,
        comments, // ✅ ADD THIS
      }),
    );
    window.addEventListener('comment:focus', handler);
    return () => window.removeEventListener('comment:focus', handler);
  }, [comments]);

  useEffect(() => {
    if (data?.comments) {
      setComments(data.comments);
    }
  }, [data]);
  useEffect(() => {
    if (!editor) return;

    const handler = () => {
      const { from, to } = editor.state.selection;

      if (from === to) {
        setBubblePos(null);
        return;
      }

      const coords = editor.view.coordsAtPos(to); // 👈 use end of selection

      setBubblePos({
        top: coords.bottom + window.scrollY + 6,
        left: coords.right + window.scrollX + 8, // 👈 push to right side
      });
    };

    editor.on('selectionUpdate', handler);
    return () => editor.off('selectionUpdate', handler);
  }, [editor]);
  if (!editor) return null;
  return (
    <div className="w-full flex flex-col">
      {/* PAPER DOCUMENT */}
      {bubblePos && (
        <div
          className="fixed z-50 flex items-center gap-2 
               bg-white border border-slate-200 
               shadow-lg rounded-full px-3 py-1.5"
          style={{
            top: bubblePos.top,
            left: bubblePos.left,
          }}
        >
          <button
            onClick={handleAddCommentClick}
            className="text-xs font-medium text-[#375AE7] hover:text-[#375AE7]"
          >
            💬 Add comment
          </button>
        </div>
      )}
      <div
        className="
          relative w-full overflow-hidden rounded-2xl
          border border-slate-200/80 bg-white
          shadow-[0_4px_24px_-4px_rgba(55,90,231,0.08),0_8px_32px_-8px_rgba(15,23,42,0.06)]
          transition-shadow hover:shadow-[0_8px_32px_-4px_rgba(55,90,231,0.12),0_12px_40px_-8px_rgba(15,23,42,0.08)]
        "
      >
        {/* Brand accent strip */}
        <div className="h-1 w-full bg-gradient-to-r from-[#375AE7] via-[#5B7BF0] to-[#375AE7]" />

        <div className="px-6 py-8 sm:px-10 sm:py-10 md:px-14 md:py-12 min-h-[640px]">
          <div className="rp-editor">
            <style>{`
              .rp-editor .ProseMirror {
                font-family: Inter, ui-sans-serif, system-ui, sans-serif;
                font-size: 15.5px;
                line-height: 1.85;
                color: #1e293b;
                outline: none;
                min-height: 520px;
              }
              .rp-editor .ProseMirror h1 {
                font-size: 1.75rem;
                font-weight: 700;
                color: #0f172a;
                margin: 0 0 0.75rem;
                letter-spacing: -0.02em;
              }
              .rp-editor .ProseMirror h2 {
                font-size: 1.35rem;
                font-weight: 650;
                color: #1e293b;
                margin: 1rem 0 0.5rem;
                letter-spacing: -0.01em;
              }
              .rp-editor .ProseMirror h3 {
                font-size: 1.1rem;
                font-weight: 600;
                color: #334155;
                margin: 0.75rem 0 0.4rem;
              }
              .rp-editor .ProseMirror p {
                margin: 0 0 0.65rem;
              }
              .rp-editor .ProseMirror ::selection {
                background: rgba(55, 90, 231, 0.18);
              }
              .rp-editor .ProseMirror:focus {
                outline: none;
              }
              .rp-editor table {
                width: 100%;
                border-collapse: separate;
                border-spacing: 0;
                margin: 12px 0;
                border-radius: 10px;
                overflow: hidden;
                border: 1px solid #e2e8f0;
              }
              .rp-editor th, .rp-editor td {
                border: 1px solid #e2e8f0;
                padding: 10px 12px;
                vertical-align: top;
                word-break: break-word;
                white-space: normal;
              }
              .rp-editor th {
                background: linear-gradient(to bottom, #f8fafc, #f1f5f9);
                font-weight: 600;
                color: #334155;
                font-size: 0.8125rem;
                text-transform: uppercase;
                letter-spacing: 0.04em;
              }
              .rp-editor td {
                background: #fff;
              }
              .rp-editor tr:hover td {
                background: #fafbfe;
              }
              .rp-editor img {
                max-width: 100%;
                height: auto;
                display: block;
                border-radius: 10px;
                margin: 8px 0;
                box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08);
              }
              .rp-editor .ProseMirror-selectednode {
                outline: 2px solid rgba(55, 90, 231, 0.45);
                outline-offset: 2px;
                border-radius: 6px;
              }
            `}</style>
            <div
              onDragOverCapture={(e) => {
                try {
                  const t = e.dataTransfer;
                  let raw = null;
                  try {
                    raw =
                      t.getData('application/x-rp-tool') ||
                      t.getData('text/plain');
                  } catch (err) {
                    raw = null;
                  }
                  if (!raw) return; // let native editor handle drags from inside editor

                  // only intercept if payload looks like our toolbox JSON
                  let payload = null;
                  try {
                    payload = JSON.parse(raw);
                  } catch (err) {
                    payload = null;
                  }
                  if (!payload) {
                    const txt = String(raw || '').toLowerCase();
                    if (!(
                      txt.includes('placeholder') ||
                      txt.includes('table') ||
                      txt.includes('image') ||
                      txt.includes('checkbox') ||
                      txt.includes('date') ||
                      txt.includes('signature')
                    ))
                      return;
                  }

                  e.preventDefault();
                  e.stopPropagation();
                  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
                } catch (err) {
                  console.log('error', err);
                }
              }}
              onDropCapture={(e) => {
                try {
                  // First, handle file drops (images)
                  try {
                    const files = e.dataTransfer.files;
                    if (files && files.length) {
                      const f = files[0];
                      if (f.type && f.type.startsWith('image/')) {
                        e.preventDefault();
                        e.stopPropagation();
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          const src = ev.target.result;
                          editor.chain().focus().setImage({ src }).run();
                        };
                        reader.readAsDataURL(f);
                        return;
                      }
                    }
                  } catch (err) {
                    console.log('error', err);
                  }

                  // Next, handle drags originating from inside the editor (move existing node)
                  let editorRaw = null;
                  try {
                    editorRaw = e.dataTransfer.getData(
                      'application/x-rp-editor',
                    );
                  } catch (err) {
                    editorRaw = null;
                  }
                  if (editorRaw) {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const payload = JSON.parse(editorRaw);
                      if (
                        payload &&
                        payload.type === 'editor-drag' &&
                        Number.isFinite(Number(payload.from))
                      ) {
                        const from = Number(payload.from);
                        // compute target pos
                        let pos = null;
                        try {
                          const coords = { left: e.clientX, top: e.clientY };

                          const fromCoords = editor.view.posAtCoords(coords);

                          if (fromCoords?.pos != null) {
                            pos = fromCoords.pos;
                          } else {
                            const el = document.elementFromPoint(
                              e.clientX,
                              e.clientY,
                            );
                            pos = el
                              ? editor.view.posAtDOM(el, 0)
                              : editor.state.doc.content.size;
                          }
                        } catch (err) {
                          pos = null;
                        }

                        const view = editor.view;
                        const state = view.state;
                        const node = state.doc.nodeAt(from);
                        if (!node) return;
                        const nodeSize = node.nodeSize;
                        let to =
                          typeof pos === 'number'
                            ? pos
                            : state.doc.content.size;
                        if (from < to) to = to - nodeSize;

                        const tr = state.tr;
                        tr.delete(from, from + nodeSize);
                        tr.insert(to, node);
                        view.dispatch(tr);
                        view.focus();
                      }
                    } catch (err) {
                      console.log('error', err);
                    }
                    return;
                  }

                  let raw = null;
                  try {
                    raw =
                      e.dataTransfer.getData('application/x-rp-tool') ||
                      e.dataTransfer.getData('text/plain');
                  } catch (err) {
                    raw = null;
                  }
                  if (!raw) return; // let native editor handle it

                  e.preventDefault();
                  e.stopPropagation();
                  if (!editor) return;

                  console.debug('[Editor] drop raw:', raw);

                  let payload = null;
                  try {
                    payload = JSON.parse(raw);
                  } catch (err) {
                    payload = null;
                  }
                  if (!payload) {
                    const txt = String(raw || '')
                      .trim()
                      .toLowerCase();
                    if (txt.includes('checkbox'))
                      payload = { type: 'checkbox' };
                    else if (txt.includes('date')) payload = { type: 'date' };
                    else if (txt.includes('signature'))
                      payload = { type: 'signature' };
                    else if (txt.includes('image')) payload = { type: 'image' };
                    else if (
                      txt.includes('placeholder') ||
                      txt.includes('field')
                    )
                      payload = {
                        type: 'placeholder',
                        key: 'field',
                        label: 'Field',
                      };
                    else payload = null;
                  }

                  if (!payload) return;

                  // try to position selection at drop coords
                  let pos = null;
                  try {
                    const coords = { left: e.clientX, top: e.clientY };

                    const fromCoords = editor.view.posAtCoords(coords);

                    if (fromCoords?.pos != null) {
                      pos = fromCoords.pos;
                    } else {
                      const el = document.elementFromPoint(
                        e.clientX,
                        e.clientY,
                      );
                      pos = el
                        ? editor.view.posAtDOM(el, 0)
                        : editor.state.doc.content.size;
                    }
                  } catch (err) {
                    pos = null;
                  }

                  if (pos !== null && typeof pos === 'number') {
                    try {
                      editor.chain().focus().setTextSelection(pos).run();
                    } catch (e) {
                      editor.chain().focus().run();
                    }
                  } else {
                    editor.chain().focus().run();
                  }

                  // handle various payload types
                  switch (payload.type) {
                    case 'placeholder':
                      editor
                        .chain()
                        .focus()
                        .insertPlaceholder({
                          id: crypto.randomUUID(),
                          key: payload.key || 'field',
                          label: payload.label || 'Field',
                          value: '',
                        })
                        .run();
                      break;
                    case 'table':
                      editor
                        .chain()
                        .focus()
                        .insertTable({
                          rows: payload.rows || 2,
                          cols: payload.cols || 2,
                          withHeaderRow: payload.withHeader || false,
                        })
                        .run();
                      break;
                    case 'image':
                      document.getElementById('tb-image-input')?.click();
                      break;
                    case 'checkbox':
                      try {
                        editor.chain().focus().insertCheckbox().run();
                      } catch (e) {
                        console.log('error', e);
                      }
                      break;
                    case 'date':
                      try {
                        editor.chain().focus().insertDate().run();
                      } catch (e) {
                        console.log('error', e);
                      }
                      break;
                    case 'signature':
                      try {
                        editor.chain().focus().insertSignature().run();
                      } catch (e) {
                        console.log('error', e);
                      }
                      break;
                    default:
                      break;
                  }
                } catch (err) {
                  console.log('error', err);
                }
              }}
            >
              <EditorContent editor={editor} />
            </div>
            {showCommentModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-3">
                {/* Modal */}
                <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-slate-50 border-b">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-[#375AE7]">
                        💬
                      </div>
                      <h3 className="font-semibold text-slate-700">
                        Add Comment
                      </h3>
                    </div>

                    <button
                      onClick={() => setShowCommentModal(false)}
                      className="text-slate-400 hover:text-slate-700 transition"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-4">
                    <textarea
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder:text-slate-400"
                      rows={4}
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Write your comment..."
                    />

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-4">
                      <button
                        onClick={() => setShowCommentModal(false)}
                        className="text-sm text-slate-500 hover:text-slate-700 transition"
                      >
                        Cancel
                      </button>

                      <button
                        onClick={saveComment}
                        className="flex items-center gap-2 bg-[#375AE7] hover:bg-[#375AE7] text-white text-sm px-4 py-2 rounded-full shadow transition disabled:opacity-50"
                        disabled={!commentText.trim()}
                      >
                        <span>Send</span>
                        <span className="text-base">➤</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <CommentsPanel
          comments={comments}
          activeComment={activeComment}
          setActiveComment={setActiveComment}
          onReply={onReply}
        />
      </div>
    </div>
  );
}
